import React, { memo, useCallback, useMemo, useState } from "react";

import * as Yup from "yup";
import { Form, Formik } from "formik";

import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { currencyFormatter } from "../helpers/utils";
import CustomTextField from "../common/CustomTextField";
import HubspotCookieBreakerInput from "../common/HubspotCookieBreakerInput";
import { Button as TailwindButton } from "../common/customDesignedComponents/button";
import {
  PROPERTY_RISK_SUPPORT_PHONE,
  PROPERTY_RISK_SUPPORT_PHONE_FORMAT,
} from "../helpers/constants";

const columns = [
  {
    id: "loandate",
    disablePadding: true,
    label: "Loan Date",
    minWidth: 110,
  },
  {
    id: "loanAmount",
    disablePadding: true,
    label: "Loan Amount",
    minWidth: 125,
  },
  {
    id: "loanType",
    disablePadding: true,
    label: "Loan Type",
    minWidth: 140,
  },
  {
    id: "lender",
    disablePadding: true,
    label: "Lender",
    minWidth: 180,
  },
];

const isReleasedLender = (lender) =>
  String(lender || "")
    .toUpperCase()
    .includes("RELEASED");

const EnhancedTableHead = memo(function EnhancedTableHead({
  compact = false,
}) {
  return (
    <TableHead>
      <TableRow>
        {columns.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={compact ? "checkbox" : "normal"}
            sx={{ minWidth: headCell?.minWidth }}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

const validationSchema = () => {
  return Yup.object().shape({
    updated_loan_amount: Yup.string("Please enter the Loan Amount.")
      .required("Loan Amount is required")
      .max(15),
  });
};

const LoanProgram = ({
  propertyLoanAmountData,
  generateReport = () => {},
  isTailwindComponent = false,
  isAdminHubAssessment = false,
}) => {
  const [isLoanAmountScreen, setIsLoanAmountScreen] = useState(false);

  const activeFinanceDetails = useMemo(() => {
    const rows = propertyLoanAmountData?.data?.finance_details;
    if (!Array.isArray(rows)) return [];
    return rows.filter((row) => !isReleasedLender(row?.Lender));
  }, [propertyLoanAmountData?.data?.finance_details]);

  const hasActiveLoans = activeFinanceDetails.length > 0;
  const propertyAddress = propertyLoanAmountData?.data?.address;
  const formattedLoanAmount = useMemo(
    () => currencyFormatter(propertyLoanAmountData?.data?.loan_amount),
    [propertyLoanAmountData?.data?.loan_amount],
  );

  const openLoanAmountScreen = useCallback(() => {
    setIsLoanAmountScreen(true);
  }, []);

  const closeLoanAmountScreen = useCallback(() => {
    setIsLoanAmountScreen(false);
  }, []);

  const updateLoanAmount = useCallback(
    (values) => {
      generateReport(parseFloat(values?.updated_loan_amount));
    },
    [generateReport],
  );

  const handleGenerateReport = useCallback(() => {
    generateReport();
  }, [generateReport]);

  const lenderCellSx = useMemo(
    () =>
      isAdminHubAssessment
        ? {
            whiteSpace: "normal",
            wordBreak: "break-word",
            minWidth: 180,
          }
        : undefined,
    [isAdminHubAssessment],
  );

  const renderActions = useCallback(
    ({ showBack = false, onPrimary, primaryLabel }) => {
      if (isAdminHubAssessment) {
        return (
          <Box className="admin-hub-modal-actions">
            {showBack ? (
              <Button
                variant="outlined"
                className="admin-hub-modal-actions__cancel"
                onClick={closeLoanAmountScreen}
              >
                Back
              </Button>
            ) : (
              <Button
                variant="outlined"
                className="admin-hub-modal-actions__cancel"
                onClick={openLoanAmountScreen}
              >
                Update loan amount
              </Button>
            )}
            <Button
              variant="contained"
              className="admin-hub-modal-actions__save"
              onClick={onPrimary}
            >
              {primaryLabel}
            </Button>
          </Box>
        );
      }

      if (isTailwindComponent) {
        return (
          <Box className="pt-4 tailwind_component">
            <TailwindButton
              variant="outline"
              className="m-1 px-8 text-md"
              onClick={onPrimary}
            >
              {primaryLabel}
            </TailwindButton>
            {!showBack && (
              <TailwindButton
                className="px-8 bg-green-500 hover:bg-green-600 text-white text-md"
                onClick={openLoanAmountScreen}
              >
                Update Loan Amount
              </TailwindButton>
            )}
          </Box>
        );
      }

      return (
        <Box className="pt-4">
          {showBack && (
            <Button
              className="popup_Btn other_popup_btn m-1"
              variant="contained"
              onClick={closeLoanAmountScreen}
            >
              Back
            </Button>
          )}
          <Button className="popup_Btn other_popup_btn m-1" onClick={onPrimary}>
            {primaryLabel}
          </Button>
          {!showBack && (
            <Button
              className="popup_Btn success_popup_btn m-1"
              onClick={openLoanAmountScreen}
            >
              Update Loan Amount
            </Button>
          )}
        </Box>
      );
    },
    [
      closeLoanAmountScreen,
      isAdminHubAssessment,
      isTailwindComponent,
      openLoanAmountScreen,
    ],
  );

  return (
    <>
      {/* Close is owned by DialogBox — do not render a second X here. */}
      <Box className={isAdminHubAssessment ? "" : "accountBoxWrapper"}>
        <Typography
          align={isAdminHubAssessment ? "left" : "center"}
          variant={isAdminHubAssessment ? "subtitle1" : "h3"}
          className={
            isAdminHubAssessment ? "admin-hub-loan-modal__address" : "py-2 mb-1"
          }
        >
          {propertyAddress}
        </Typography>
        <Box className={isAdminHubAssessment ? "" : "mb-4"}>
          <Typography
            align={isAdminHubAssessment ? "left" : "center"}
            className={
              isAdminHubAssessment
                ? "admin-hub-loan-modal__description"
                : "fw-400 para m-0"
            }
          >
            {isLoanAmountScreen ? (
              "The risk report will be generated using the loan amount you enter below."
            ) : hasActiveLoans ? (
              <>
                Below are the loan details for this property. The risk report is
                being generated based on a loan amount of{" "}
                <strong>{formattedLoanAmount}</strong>
                . If the loan amount is inaccurate, you may update it prior to
                generating the report.
              </>
            ) : null}
          </Typography>
        </Box>
        {isLoanAmountScreen ? (
          <div className={isAdminHubAssessment ? "" : "form-body"}>
            <Formik
              enableReinitialize
              validateOnChange
              initialValues={{
                updated_loan_amount:
                  propertyLoanAmountData?.data?.loan_amount || "",
              }}
              validationSchema={validationSchema}
              onSubmit={updateLoanAmount}
            >
              {({ values, setFieldValue, touched, errors }) => {
                return (
                  <Form id="loan-program-form">
                    <HubspotCookieBreakerInput />

                    <Grid
                      container
                      spacing={2}
                      textAlign={isAdminHubAssessment ? "left" : "center"}
                      display={"flex"}
                      justifyContent={
                        isAdminHubAssessment ? "flex-start" : "center"
                      }
                      alignItems={"center"}
                    >
                      <Grid item xs={12} md={isAdminHubAssessment ? 5 : 6}>
                        {isAdminHubAssessment ? (
                          <TextField
                            fullWidth
                            size="small"
                            label="Loan amount"
                            name="updated_loan_amount"
                            type="number"
                            value={values.updated_loan_amount}
                            onChange={(event) =>
                              setFieldValue(
                                "updated_loan_amount",
                                event.target.value,
                              )
                            }
                            error={Boolean(
                              touched.updated_loan_amount &&
                                errors.updated_loan_amount,
                            )}
                            helperText={
                              touched.updated_loan_amount &&
                              errors.updated_loan_amount
                            }
                          />
                        ) : (
                          <CustomTextField
                            id="updatedLoanAmount"
                            name="updated_loan_amount"
                            placeholder="Enter the Loan amount"
                            type="number"
                            validation={{
                              values,
                              setFieldValue,
                              touched,
                              errors,
                            }}
                            otherProps={{
                              onChange: (event) => {
                                setFieldValue(
                                  "updated_loan_amount",
                                  event.target.value,
                                );
                              },
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                    {isAdminHubAssessment ? (
                      <Box className="admin-hub-modal-actions">
                        <Button
                          variant="outlined"
                          className="admin-hub-modal-actions__cancel"
                          onClick={closeLoanAmountScreen}
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          className="admin-hub-modal-actions__save"
                        >
                          Generate report
                        </Button>
                      </Box>
                    ) : isTailwindComponent ? (
                      <Box className="text-center pt-4 tailwind_component">
                        <TailwindButton
                          type="button"
                          variant="outline"
                          className="m-1 px-8 text-md"
                          onClick={closeLoanAmountScreen}
                        >
                          Back
                        </TailwindButton>
                        <TailwindButton
                          type="submit"
                          className="px-8 bg-green-500 hover:bg-green-600 text-white text-md"
                        >
                          Generate Report
                        </TailwindButton>
                      </Box>
                    ) : (
                      <Box className="text-center pt-4">
                        <Button
                          className="popup_Btn other_popup_btn"
                          variant="contained"
                          onClick={closeLoanAmountScreen}
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          className="popup_Btn success_popup_btn mx-2"
                          variant="contained"
                        >
                          Generate Report
                        </Button>
                      </Box>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        ) : (
          <>
            <Box
              className={
                isAdminHubAssessment ? "admin-hub-loan-modal__table" : ""
              }
            >
              <TableContainer
                sx={{ maxHeight: 400 }}
                className={isAdminHubAssessment ? "" : "d-none d-sm-block"}
              >
                <Table
                  size={isAdminHubAssessment ? "small" : "medium"}
                  sx={{ minWidth: isAdminHubAssessment ? "100%" : 600 }}
                  aria-labelledby="tableTitle"
                  stickyHeader
                >
                  <EnhancedTableHead compact={isAdminHubAssessment} />
                  <TableBody>
                    {propertyLoanAmountData?.success &&
                      activeFinanceDetails.map((row, index) => (
                        <TableRow tabIndex={-1} key={index}>
                          <TableCell align="left">
                            {row?.["Loan Date"] || "-"}
                          </TableCell>
                          <TableCell align="left">
                            {row?.["Loan Amount"]
                              ? `${row?.["Loan Amount"]}`
                              : "-"}
                          </TableCell>
                          <TableCell align="left">
                            {row?.["Loan Type"] || "-"}
                          </TableCell>
                          <TableCell align="left" sx={lenderCellSx}>
                            {row?.Lender || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                {!hasActiveLoans && (
                  <Typography
                    className={
                      isAdminHubAssessment
                        ? "p-4 text-center"
                        : "p-5 text-center"
                    }
                    variant={isAdminHubAssessment ? "body2" : "h6"}
                  >
                    Currently, we have not found any active loan associated with
                    this property in our records.
                  </Typography>
                )}
              </TableContainer>
            </Box>
            {!isAdminHubAssessment && (
              <Box>
                <Box className="d-block d-sm-none mobile-table bg-white px-0 mb-3 pt-2 ">
                  {activeFinanceDetails.map((row, index) => (
                    <React.Fragment key={index}>
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        justifyContent={"space-between"}
                        spacing={0}
                        p={1.4}
                        pt={2}
                        mb={2}
                      >
                        <Stack direction="row" alignItems="baseline">
                          <div>
                            <Stack direction="column" px={1}>
                              <Typography
                                className="key fw-500 pb-0"
                                sx={{ fontSize: "12px !important" }}
                              >
                                Loan Date
                              </Typography>
                              <Stack direction="column" spacing={0.5}>
                                <Typography className="value">
                                  {row?.["Loan Recording Date"] || "-"}
                                </Typography>
                              </Stack>

                              <Stack direction="row" className="">
                                <Stack direction="column" spacing={0.5}>
                                  <Typography
                                    className="key fw-500"
                                    sx={{ fontSize: "12px !important" }}
                                  >
                                    Loan Amount
                                  </Typography>
                                  <Typography className="value">
                                    {row?.["Loan Amount"]
                                      ? `${row?.["Loan Amount"]}`
                                      : "-"}
                                  </Typography>
                                </Stack>
                                <Stack direction="column" spacing={0.5}>
                                  <Typography
                                    className="key fe-500"
                                    sx={{ fontSize: "12px !important" }}
                                  >
                                    Loan Type
                                  </Typography>
                                  <Typography className="value">
                                    {row?.["Loan Type"] || "-"}
                                  </Typography>
                                </Stack>
                              </Stack>

                              <Stack direction="column">
                                <Typography
                                  className="key fw-500"
                                  sx={{ fontSize: "12px !important" }}
                                >
                                  Lender
                                </Typography>
                                <Stack direction="column" spacing={0.5}>
                                  <Typography className="value">
                                    {row?.Lender || "-"}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Stack>
                          </div>
                        </Stack>
                      </Stack>

                      {index < activeFinanceDetails.length - 1 && (
                        <Divider
                          className="my-2 mt-3"
                          sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
                        />
                      )}
                    </React.Fragment>
                  ))}

                  {!hasActiveLoans && (
                    <Typography
                      className="d-block d-sm-none p-5 text-center"
                      variant="h6"
                    >
                      Currently, we have not found any active loan associated
                      with this property in our records.
                    </Typography>
                  )}
                </Box>

                {renderActions({
                  onPrimary: handleGenerateReport,
                  primaryLabel: "Generate Report",
                })}
              </Box>
            )}
            {isAdminHubAssessment &&
              renderActions({
                onPrimary: handleGenerateReport,
                primaryLabel: "Generate report",
              })}
          </>
        )}
      </Box>
      {!isLoanAmountScreen && (
        <>
          {!isAdminHubAssessment && <Divider />}
          <Box
            className={
              isAdminHubAssessment
                ? "admin-hub-loan-modal__footnote"
                : "mt-3 mx-5 mb-1"
            }
          >
            <Typography
              variant="body2"
              align={isAdminHubAssessment ? "left" : "center"}
              className={isAdminHubAssessment ? "" : "fw-500 para m-0"}
            >
              If the loan reporting is not accurate, please contact customer
              service at{" "}
              <a href={`tel:${PROPERTY_RISK_SUPPORT_PHONE}`}>
                {PROPERTY_RISK_SUPPORT_PHONE_FORMAT}
              </a>{" "}
              as your previous lender did not file a release of lien.
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

export default memo(LoanProgram);
